/**
 * Client des routes /api/* du backend Quantime.
 * Toutes les requêtes envoient du WAV mono 16-bit sous le champ `file`.
 */
import type { Analysis, HealthInfo, QuantizeMeta } from '../types';
import { track } from '../lib/track';

const FN_NAMES: Record<string, string> = {
  '/analyze': 'analyse',
  '/quantize': 'calage',
  '/denoise': 'nettoyage',
};

const BASE = (import.meta.env.VITE_API_BASE ?? '/api').replace(/\/$/, '');

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = 'ApiError';
  }
}

async function readError(res: Response): Promise<string> {
  try {
    const body = await res.json();
    if (typeof body?.detail === 'string') return body.detail;
  } catch {
    /* pas de JSON */
  }
  if (res.status === 413) return 'Fichier trop volumineux (max 60 Mo).';
  if (res.status === 504) return 'Le traitement a pris trop de temps (audio trop long ?).';
  if (res.status >= 500) return `Le serveur a rencontré une erreur (HTTP ${res.status}).`;
  return `Erreur ${res.status}.`;
}

function form(fields: Record<string, string | number | boolean>, wav: Blob): FormData {
  const fd = new FormData();
  fd.append('file', wav, 'audio.wav');
  for (const [k, v] of Object.entries(fields)) fd.append(k, String(v));
  return fd;
}

async function post(path: string, fd: FormData, signal?: AbortSignal): Promise<Response> {
  const fonction = FN_NAMES[path] ?? path;
  track('traitement-lance', { fonction });
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, { method: 'POST', body: fd, signal });
  } catch (e) {
    track('traitement-echec', { fonction, code: 'reseau' });
    throw e;
  }
  if (!res.ok) {
    track('traitement-echec', { fonction, code: res.status });
    throw new ApiError(res.status, await readError(res));
  }
  track('traitement-termine', { fonction });
  return res;
}

export async function health(signal?: AbortSignal): Promise<HealthInfo> {
  const res = await fetch(`${BASE}/health`, { signal });
  if (!res.ok) throw new ApiError(res.status, await readError(res));
  return res.json();
}

export async function analyze(
  wav: Blob,
  sel?: { start: number; end: number },
  signal?: AbortSignal,
): Promise<Analysis> {
  const fd = form({ sel_start: sel?.start ?? 0, sel_end: sel?.end ?? 0 }, wav);
  const res = await post('/analyze', fd, signal);
  const raw = await res.json();
  return {
    duration: raw.duration,
    sampleRate: raw.sample_rate,
    bpm: raw.bpm ?? null,
    key: raw.key ?? null,
    engine: raw.engine ?? null,
    onsets: raw.onsets ?? [],
    strengths: raw.onset_strengths ?? [],
  };
}

export interface DenoiseParams {
  amount: number;
  useHighpass: boolean;
  useHum: boolean;
  humBase?: number;
  profile?: { start: number; end: number } | null;
}

export async function denoise(
  wav: Blob,
  p: DenoiseParams,
  signal?: AbortSignal,
): Promise<{ wav: ArrayBuffer; meta: unknown }> {
  const fd = form(
    {
      amount: p.amount,
      use_highpass: p.useHighpass,
      use_hum: p.useHum,
      hum_base: p.humBase ?? 50,
      profile_start: p.profile?.start ?? -1,
      profile_end: p.profile?.end ?? -1,
    },
    wav,
  );
  const res = await post('/denoise', fd, signal);
  return { wav: await res.arrayBuffer(), meta: parseMeta(res) };
}

export interface QuantizeParams {
  bpm: number;
  division: number;
  strength: number;
  mode: 'warp' | 'slice';
  selection?: { start: number; end: number } | null;
  fillGaps?: boolean;
}

export async function quantize(
  wav: Blob,
  p: QuantizeParams,
  signal?: AbortSignal,
): Promise<{ wav: ArrayBuffer; meta: QuantizeMeta | null }> {
  const fd = form(
    {
      bpm: p.bpm,
      division: p.division,
      strength: p.strength,
      mode: p.mode,
      sel_start: p.selection?.start ?? 0,
      sel_end: p.selection?.end ?? 0,
      fill_gaps: p.fillGaps ?? true,
    },
    wav,
  );
  const res = await post('/quantize', fd, signal);
  return { wav: await res.arrayBuffer(), meta: parseMeta(res) as QuantizeMeta | null };
}

function parseMeta(res: Response): unknown {
  const raw = res.headers.get('X-Quantime-Meta');
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}
